import React from 'react';
import { LockIcon, ShieldIcon, EyeOffIcon, ToggleLeftIcon, SparklesIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const privacyFeatures = [{
  title: 'No Storage or Selling',
  description: 'Emigo does not store or sell your messages and has no access to your chat history or what others send you. It only processes what you type or upload, such as screenshots you choose to analyze, when you actively use features like Reframe or Interpret.',
  icon: ShieldIcon
}, {
  title: 'Anonymous Processing',
  description: "Every message sent through Emigo, yours and everyone else's, is processed anonymously. They all flow through the same channel, with no way to tell who sent what.",
  icon: EyeOffIcon
}, {
  title: 'Opt-Out by Default',
  description: 'By default, you are opted out of all data sharing with Emigo itself. When you use a feature, your text is transmitted securely to our trusted technology partner. This data is handled under strict confidentiality and is not used to identify you.',
  icon: ToggleLeftIcon
}, {
  title: 'Future Optional Features',
  description: 'In the future, we may introduce optional features such as emotional insights or personalized analytics. These will always be opt-in, and you will have full control over what you share. Uninstalling the app clears all local data immediately.',
  icon: SparklesIcon
}];
const PrivacyCard = ({
  feature
}: {
  feature: (typeof privacyFeatures)[0];
}) => {
  const Icon = feature.icon;
  return <div className="group flex-1 min-w-0" data-id="element-303">
      <div className="bg-white rounded-2xl p-5 border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col" data-id="element-304">
        {/* Icon */}
        <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-4 group-hover:bg-[#E87B5C]/5 group-hover:border-[#E87B5C]/20 transition-all duration-300" data-id="element-305">
          <Icon className="h-5 w-5 text-[#E87B5C]" data-id="element-306" />
        </div>

        {/* Title */}
        <h3 className="text-base font-semibold text-gray-900 mb-2 group-hover:text-[#E87B5C] transition-colors duration-300" style={{
        fontFamily: "'DM Serif Display', serif"
      }} data-id="element-307">
          {feature.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-gray-600 leading-relaxed flex-grow" data-id="element-308">
          {feature.description}
        </p>
      </div>
    </div>;
};
export function PrivacySection() {
  const navigate = useNavigate();
  return <section className="py-10 md:py-14 bg-white" data-id="element-309">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-310">
        {/* Title area */}
        <div className="mb-10" data-id="element-311">
          <h2 className="text-3xl md:text-4xl font-semibold text-gray-900" style={{
          fontFamily: "'DM Serif Display', serif"
        }} data-id="element-312">
            Your Privacy Comes First
          </h2>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-3" data-id="element-313">
            <p className="text-base text-gray-600" data-id="element-314">Your words stay private.</p>
            <button onClick={() => navigate('/privacy-policy')} className="inline-flex items-center gap-1.5 text-[#E87B5C] hover:text-[#D16A4A] transition-colors font-medium text-sm" data-id="element-315">
              <LockIcon className="w-4 h-4" data-id="element-316" />
              <span className="underline underline-offset-2" data-id="element-317">
                See our full privacy policy
              </span>
            </button>
          </div>
        </div>

        {/* Four cards in a row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-318">
          {privacyFeatures.map((feature, index) => <PrivacyCard key={index} feature={feature} data-id="element-319" />)}
        </div>
      </div>
    </section>;
}
export default PrivacySection;